import { z } from "zod";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "../date-time/iso-and-unix";
import { emailFactory, emailZodSchema, type Email } from "../email";
import { entityStateZodSchema, type EntityState } from "../entity-status";
import {
    phoneFactory,
    phoneZodSchema,
    type Phone,
    type PhoneNumber,
} from "../phone";
import { providerIdZodSchema, type ProviderId } from "../providers/provider-id";
import {
    customerIdFactory,
    customerIdZodSchema,
    type CustomerId,
} from "./customer-id";

type Customer = {
    _id: CustomerId;
    _type: "CUSTOMER";
    _status: EntityState;
    _createdAt: IsoAndUnixTimestamp;
    providerId: ProviderId;
    firstName?: string;
    lastName?: string;
    email?: Email;
    phone: Phone;
};

const customerFactory = (params: {
    _id?: CustomerId;
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    phoneNumber: PhoneNumber;
    providerId: ProviderId;
}): Customer => {
    return {
        _id: params._id ?? customerIdFactory(),
        _type: "CUSTOMER",
        _status: "ACTIVE",
        _createdAt: nowIsoAndUnix(),
        firstName: params.firstName,
        lastName: params.lastName,
        providerId: params.providerId,
        email: params.emailAddress
            ? emailFactory({
                  emailAddress: params.emailAddress,
                  verified: true,
              })
            : undefined,
        phone: phoneFactory({
            phoneNumber: params.phoneNumber,
            verified: true,
        }),
    };
};

const customerZodSchema: z.ZodSchema<Customer> = z.object({
    _id: customerIdZodSchema,
    _type: z.literal("CUSTOMER"),
    _status: entityStateZodSchema,
    _createdAt: isoAndUnixZodSchema,
    providerId: providerIdZodSchema,
    firstName: z.string(),
    lastName: z.string(),
    email: emailZodSchema,
    phone: phoneZodSchema,
});

const isCustomer = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customer: any,
    exhaustiveCheck = false,
): customer is Customer => {
    return exhaustiveCheck
        ? customerZodSchema.safeParse(customer).success
        : customer._type === "CUSTOMER";
};
export { customerFactory, customerZodSchema, isCustomer };
export type { Customer };
